import React from 'react'

import {
  LAUNCH_ROOT_URL,
  LAUNCH_BASE_URLS,
  SALES_ANALYTICS_URL,
  SALES_ANALYTICS_URLS,
  INVENTORY_URLS,
  MARKET_INSIGHTS_URLS,
  LISTING_BUILDER_BASE,
  PRODUCT_INSIGHTS_URL,
  SHARE_OF_VOICE_URL,
  SALES_ESTIMATOR_URL,
  LISTING_GRADER_BASE,
  REVIEW_ANALYSIS_BASE,
  PRODUCT_RESEARCH_URLS,
  RANK_TRACKER_BASE,
  SUPPLIER_URLS,
  KEYWORD_LISTS_URL,
  KEYWORD_SCOUT_URL,
  ACADEMY_URL,
  EXTENSION_URL,
  ADMIN_URL,
  DASHBOARD_URL,
  PAGE_NAMES,
  ADVERTISING_URL,
  ADVERTISING_URLS,
  PRODUCT_COSTS_AND_SETTINGS_URL,
  SETTINGS_URLS,
  TEAM_SETTINGS_URL,
  INVENTORY_REIMBURSEMENTS_URL
} from 'constants/routes'
import { ACCOUNT_SETTINGS_ICONS } from 'constants/account'
import { COBALT_APP_TYPE, DEFAULT_APP_TYPE } from 'constants/themes'
import { Features } from 'constants/feature_names'
import { PERMISSION_KEYS } from 'constants/account/permissions'
import { NAVIGATION_DATA_ATTRIBUTE_IDS } from 'constants/data_attributes/navigation'
import { Icon } from '@junglescout/edna'

import { isBeforeToday } from 'helpers/date'
import { CNThemeIcon } from 'icons/CNThemeIcon/CNThemeIcon'
import { isFeatureEnabled } from './features'

export const buildMenuItems = ({ t, flagData }) => {
  function renderBadge(end, key, isBeta = false) {
    if (!isBeforeToday(end)) {
      if (isBeta) {
        return t(key, 'BETA')
      }
      return t(key, 'NEW')
    }
    return null
  }

  const isAiSummaryNewTagEnabled = isFeatureEnabled(
    Features.PROFIT_OVERVIEW_AI_SUMMARY,
    flagData
  )

  const isReviewAnalysisEnabled = isFeatureEnabled(
    Features.REVIEW_ANALYSIS_POC,
    flagData
  )

  const aiSummaryNewTag = isAiSummaryNewTagEnabled
    ? renderBadge('2023-08-28', 'sidebar:toolbox.header.isNew')
    : null

  const pageNames = PAGE_NAMES(t)

  return [
    {
      name: pageNames.marketInsights,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.MarketInsights,
      icon: <Icon name="NAV_MARKET_INSIGHT" />,
      type: 'fill',
      url: MARKET_INSIGHTS_URLS.segmentManager,
      appAccessList: [COBALT_APP_TYPE],
      permissionKey: PERMISSION_KEYS.MARKET_INSIGHTS,
      children: [
        {
          name: pageNames.segmentManager,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.SegmentManager,
          url: MARKET_INSIGHTS_URLS.segmentManager,
          appAccessList: [COBALT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.SEGMENT_MANAGER,
          description: t(
            'global_search:description.marketInsights',
            'Manage your segments'
          ),
          keywords: ['insights']
        },
        {
          name: pageNames.shareAndTrends,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ShareAndTrends,
          url: MARKET_INSIGHTS_URLS.shareAndTrendsBase,
          appAccessList: [COBALT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.SHARE_AND_TRENDS,
          isCurrentPath: currentPath =>
            !!currentPath.startsWith(MARKET_INSIGHTS_URLS.shareAndTrendsBase),
          description: t(
            'global_search:description.shareAndTrends',
            'Understand your market share'
          )
        }
      ]
    },
    {
      name: pageNames.productInsights,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ProductInsights,
      icon: <Icon name="NAV_PROD_INSIGHTS" />,
      type: 'fill',
      appAccessList: [COBALT_APP_TYPE],
      url: PRODUCT_INSIGHTS_URL,
      permissionKey: PERMISSION_KEYS.PRODUCT_INSIGHTS,
      description: t(
        'global_search:description.productInsights',
        'Look up and track products to get insights'
      )
    },
    {
      name: pageNames.shareOfVoice,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ShareOfVoice,
      icon: <Icon name="NAV_SHARE_VOICE" />,
      type: 'fill',
      features: [Features.SHARE_OF_VOICE_HIDE_FEATURE],
      appAccessList: [COBALT_APP_TYPE],
      url: SHARE_OF_VOICE_URL,
      permissionKey: PERMISSION_KEYS.SHARE_OF_VOICE,
      description: t(
        'global_search:description.shareOfVoice',
        'Look up new products'
      )
    },
    {
      name: pageNames.dashboard,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.HomeDashboard,
      icon: <Icon name="NAV_HOME" />,
      type: 'fill',
      url: DASHBOARD_URL,
      description: t(
        'global_search:description.homeDashboard',
        'Your homepage and preview hub for our features.'
      )
    },
    {
      isNew:
        !!isReviewAnalysisEnabled &&
        renderBadge('2023-08-29', 'sidebar:toolbox.header.isNew'),
      name: pageNames.toolbox,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Toolbox,
      icon: <Icon name="NAV_TOOLBOX" />,
      type: 'fill',
      url: '/#',
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE],
      features: [Features.SALES_ESTIMATOR],
      permissionKey: PERMISSION_KEYS.TOOLBOX,
      children: [
        {
          name: pageNames.salesEstimator,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.SalesEstimator,
          url: SALES_ESTIMATOR_URL,
          features: [Features.SALES_ESTIMATOR],
          appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.TOOLBOX,
          description: t(
            'global_search:description.salesEstimator',
            'Average monthly sales numbers for specific Amazon categories'
          )
        },
        {
          name: pageNames.listingGrader,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ListingGrader,
          url: LISTING_GRADER_BASE,
          features: [Features.LISTING_GRADER_LAUNCH],
          appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.TOOLBOX,
          description: t(
            'global_search:description.listingGrader',
            'Listing Optimization Score'
          )
        },
        {
          isNew: renderBadge('2023-08-28', 'generic.BetaTag', true),
          name: pageNames.reviewAnalysis,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ReviewAnalysis,
          url: REVIEW_ANALYSIS_BASE,
          features: [Features.REVIEW_ANALYSIS_POC],
          appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE],
          permissionKey:
            PERMISSION_KEYS.TOOLBOX && PERMISSION_KEYS.REVIEW_ANALYSIS,
          description: t(
            'global_search:description.reviewAnalysis',
            `Get an in-depth analysis of an ASIN's reviews.`
          )
        }
      ]
    },
    {
      name: pageNames.finder,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ProductResearch,
      icon: <Icon name="NAV_PROD_RESEARCH" />,
      type: 'fill',
      url: PRODUCT_RESEARCH_URLS.database,
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      children: [
        {
          name: pageNames.db,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ProductDatabase,
          url: PRODUCT_RESEARCH_URLS.database,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.FIND_PRODUCTS,
          description: t('global_search:description.db', 'Search for products')
        },
        {
          name: pageNames.tracker,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ProductTracker,
          url: PRODUCT_RESEARCH_URLS.tracker,
          permissionKey: PERMISSION_KEYS.FIND_PRODUCTS,
          description: t(
            'global_search:description.productTracker',
            'Track, organize, group, and compare your product ideas.'
          )
        },
        {
          name: pageNames.opFinder,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.OpportunityFinder,
          url: PRODUCT_RESEARCH_URLS.opportunityFinder,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.FIND_PRODUCTS,
          description: t(
            'global_search:description.opFinder',
            'Find niches with high demand and low competition.'
          )
        },
        {
          name: pageNames.categoryTrends,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.CategoryTrends,
          url: PRODUCT_RESEARCH_URLS.categoryTrends,
          description: t(
            'global_search:description.categoryTrends',
            'Use historical trends to identify new niches for your launches.'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.FIND_PRODUCTS
    },
    {
      name: pageNames.supplier,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Suppliers,
      icon: <Icon name="NAV_SUPPLIERS" />,
      type: 'fill',
      url: SUPPLIER_URLS.supplier,
      children: [
        {
          name: pageNames.supplierDb,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.SupplierDatabase,
          url: SUPPLIER_URLS.supplier,
          children: [
            { url: SUPPLIER_URLS.supplier },
            { url: SUPPLIER_URLS.results },
            { url: SUPPLIER_URLS.view }
          ],
          permissionKey: PERMISSION_KEYS.SUPPLIERS,
          description: t(
            'global_search:description.supplierDb',
            'Find suppliers used by the best brands to source your inventory.'
          )
        },
        {
          name: pageNames.supplierTracker,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.SupplierTracker,
          url: SUPPLIER_URLS.tracker,
          permissionKey: PERMISSION_KEYS.SUPPLIERS,
          description: t(
            'global_search:description.supplierTracker',
            'Track, compare, organize and manage suppliers in one place.'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.SUPPLIERS
    },
    {
      name: pageNames.keyword,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Keywords,
      icon: <Icon name="NAV_KEYWORDS" />,
      url: KEYWORD_SCOUT_URL,
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      children: [
        {
          name: pageNames.keywordScout,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.KeywordScout,
          url: KEYWORD_SCOUT_URL,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.KEYWORDS,
          description: t(
            'global_search:description.keywordScout',
            'Track down applicable search information for keywords or asins'
          )
        },
        {
          name: pageNames.keywordList,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.KeywordLists,
          url: KEYWORD_LISTS_URL,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.KEYWORDS,
          isCurrentPath: currentPath =>
            !!currentPath.startsWith(KEYWORD_LISTS_URL),
          description: t(
            'global_search:description.keywordList',
            'Create your keyword list'
          )
        },
        {
          name: pageNames.listingBuilder,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ListingBuilder,
          url: LISTING_BUILDER_BASE,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.KEYWORDS,
          isCurrentPath: currentPath =>
            !!currentPath.startsWith(LISTING_BUILDER_BASE),
          description: t(
            'global_search:description.listingBuilder',
            'Product listings based on the top keywords from your search'
          )
        },
        {
          name: pageNames.rankTracker,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.RankTracker,
          url: RANK_TRACKER_BASE,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.KEYWORDS,
          description: t(
            'global_search:description.rankTracker',
            'Track how you or your competitor rank for your keywords'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.KEYWORDS
    },
    {
      name: pageNames.marketing,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Marketing,
      icon: <Icon name="NAV_MARKETING" />,
      url: LAUNCH_ROOT_URL,
      appAccessList: [DEFAULT_APP_TYPE],
      type: 'fill',
      children: [
        {
          name: pageNames.promo,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Promotions,
          url: LAUNCH_BASE_URLS.promotions,
          permissionKey: PERMISSION_KEYS.LAUNCH,
          description: t(
            'global_search:description.promotions',
            `Promote your products to propel your products' sales and rankings.`
          )
        },
        {
          name: pageNames.reviewAutomation,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ReviewAutomation,
          url: LAUNCH_BASE_URLS.reviewAutomation,
          permissionKey: PERMISSION_KEYS.LAUNCH,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          description: t(
            'global_search:description.reviewAutomation',
            'Automate asking customers for reviews'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.LAUNCH
    },
    {
      name: pageNames.marketing,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Marketing,
      icon: <Icon name="NAV_MARKETING" />,
      url: LAUNCH_ROOT_URL,
      appAccessList: [COBALT_APP_TYPE],
      type: 'fill',
      children: [
        {
          name: pageNames.reviewAutomation,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ReviewAutomation,
          url: LAUNCH_BASE_URLS.reviewAutomation,
          permissionKey: PERMISSION_KEYS.LAUNCH,
          appAccessList: [COBALT_APP_TYPE]
        }
      ],
      permissionKey: PERMISSION_KEYS.LAUNCH
    },
    {
      isNew: aiSummaryNewTag,
      name: pageNames.analytics,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.SalesAnalytics,
      icon: <Icon name="NAV_SALES_ANALYTICS" />,
      url: SALES_ANALYTICS_URL,
      type: 'fill',
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      children: [
        {
          isNew: aiSummaryNewTag,
          name: pageNames.profit,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ProfitOverview,
          url: SALES_ANALYTICS_URLS.profitOverview,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.SALES_ANALYTICS,
          description: t(
            'global_search:description.profit',
            'View sales and profit data over time'
          ),
          keywords: [
            'insights',
            'products',
            'multi-product view',
            'ppc',
            'costs',
            'units sold'
          ]
        },
        {
          name: pageNames.profitAndLoss,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.PAndLStatement,
          url: SALES_ANALYTICS_URLS.profitAndLoss,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.SALES_ANALYTICS,
          description: t(
            'global_search:description.profitAndLoss',
            'Detailed Profit & Loss statement'
          )
        },
        {
          name: pageNames.expenses,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.OtherTransactions,
          url: SALES_ANALYTICS_URLS.expenses,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.SALES_ANALYTICS,
          description: t(
            'global_search:description.expenses',
            'Customize entries for revenue and expenses'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.SALES_ANALYTICS
    },
    {
      name: pageNames.advertising,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Advertising,
      icon: <Icon name="NAV_ADS" />,
      url: ADVERTISING_URL,
      type: 'fill',
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      children: [
        {
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.AdsAnalytics,
          name: pageNames.adsAnalytics,
          permissionKey: PERMISSION_KEYS.ADVERTISING,
          url: ADVERTISING_URLS.analytics,
          description: t(
            'global_search:description.adsAnalytics',
            'Learn how your advertising activities impact your sales.'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.ADVERTISING,
      description: t(
        'global_search:description.adsAnalytics',
        'Learn how your advertising activities impact your sales and bottom line'
      )
    },
    {
      // TODO: move to mail once 'v2' is released
      name: pageNames.manageProd,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.ManageProducts,
      icon: <Icon name="NAV_MANAGE_PROD" />,
      url: PRODUCT_COSTS_AND_SETTINGS_URL,
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      children: [
        {
          name: pageNames.inventory,
          dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.InventoryManager,
          url: INVENTORY_URLS.inventoryForecast,
          type: 'fill',
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.MY_PRODUCTS,
          description: t(
            'global_search:description.inventory',
            'Accurately predict how much stock you will need and when to order it'
          )
        },
        {
          name: pageNames.costsAndSettings,
          url: PRODUCT_COSTS_AND_SETTINGS_URL,
          appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
          permissionKey: PERMISSION_KEYS.MY_PRODUCTS,
          description: t(
            'global_search:description.costs',
            'Get a comprehensive overview of your Product Costs'
          )
        }
      ],
      permissionKey: PERMISSION_KEYS.MY_PRODUCTS
    },
    {
      isNew: renderBadge('2023-08-28', 'sidebar:toolbox.header.isNew'),
      name: pageNames.fbaReimbursements,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.FBAReimbursements,
      url: INVENTORY_REIMBURSEMENTS_URL,
      features: [Features.INVENTORY_REIMBURSEMENTS],
      icon: <Icon name="NAV_REIMBURSEMENT" />,
      type: 'fill'
    },
    {
      name: pageNames.academy,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.Academy,
      icon: <Icon name="NAV_ACADEMY" />,
      url: ACADEMY_URL,
      type: 'fill',
      separator: true,
      permissionKey: PERMISSION_KEYS.ACADEMY,
      description: t(
        'global_search:description.academy',
        'Learn about Amazon and our platform.'
      )
    },
    {
      name: pageNames.extension,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.TheExtension,
      icon: <Icon name="NAV_EXTENSION" />,
      url: EXTENSION_URL,
      type: 'fill',
      separator: true,
      description: t(
        'global_search:description.extension',
        'Use the extension to validate ideas for products.'
      )
    },
    {
      name: pageNames.admin,
      dataId: NAVIGATION_DATA_ATTRIBUTE_IDS.AdminPanel,
      icon: <Icon name="NAV_ADMIN_PANEL" />,
      url: ADMIN_URL,
      appAccessList: [COBALT_APP_TYPE, DEFAULT_APP_TYPE],
      separator: true,
      description: t(
        'global_search:description.adminPanel',
        'Access to special feature exclusive to Admin Users.'
      )
    }
  ]
}

export const buildSettingsItems = ({ t }) => {
  return [
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.account} />,
      title: t('account:MyAccount.menu.accountInfo', 'Account Information'),
      description: t(
        'account:MyAccount.menu.accountInfoDesc',
        'Manage your account and permissions'
      ),
      url: SETTINGS_URLS.accountInfo,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.billing} />,
      title: t('account:MyAccount.menu.billingInfo', 'Billing Information'),
      description: t(
        'account:MyAccount.menu.billingInfoDesc',
        'Manage your billing information and view your invoices'
      ),
      url: SETTINGS_URLS.billing,
      appAccessList: [DEFAULT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.subscriptions} />,
      title: t('account:MyAccount.menu.subscriptions', 'Subscriptions'),
      description: t(
        'account:MyAccount.menu.subscriptionsDesc',
        'Manage or update your Jungle Scout subscriptions'
      ),
      url: SETTINGS_URLS.subscriptions,
      appAccessList: [DEFAULT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.email} />,
      title: t('account:MyAccount.menu.emailSettings', 'Email Settings'),
      description: t(
        'account:MyAccount.menu.emailSettingsDesc',
        'Manage email notifications'
      ),
      url: SETTINGS_URLS.emailSettings,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.alerts} />,
      title: t('account:MyAccount.menu.alertsSettings', 'Alert Settings'),
      description: t(
        'account:MyAccount.menu.alertsSettingsDesc',
        'Manage how you get alerted when a product changes'
      ),
      url: SETTINGS_URLS.alertSettings,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.amazon} />,
      title: t('account:MyAccount.menu.amazonSettings', 'Amazon Settings'),
      description: t(
        'account:MyAccount.menu.amazonSettingsDesc',
        'Manage your Amazon Seller Central account information'
      ),
      url: SETTINGS_URLS.amazonSettings,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.api} />,
      title: t('account:MyAccount.menu.apiKeys', 'API'),
      description: t(
        'account:MyAccount.menu.apiKeysDesc',
        'Manage API keys and review activity'
      ),
      url: SETTINGS_URLS.apiKeys,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.team} />,
      title: t('account:MyAccount.menu.team', 'User & Team Management'),
      description: t(
        'account:MyAccount.menu.teamDesc',
        'Manage your user and team accounts.'
      ),
      url: `${TEAM_SETTINGS_URL}?tab=users`,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    },
    {
      icon: <CNThemeIcon />,
      title: '主题外观',
      description: '更改外观风格设置',
      url: SETTINGS_URLS.cnTheme,
      appAccessList: [DEFAULT_APP_TYPE]
    },
    {
      icon: <Icon name={ACCOUNT_SETTINGS_ICONS.languages} />,
      title: t('account:MyAccount.menu.languages', 'Languages'),
      description: t(
        'account:MyAccount.menu.languagesDesc',
        'Change the language that is displayed throughout the app'
      ),
      url: SETTINGS_URLS.languages,
      appAccessList: [DEFAULT_APP_TYPE, COBALT_APP_TYPE]
    }
  ]
}
